import React, { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { nombreArchivoImagen } from '../utils/nombreImagen';

const MagicWandModal = ({ isOpen, onClose, product, onImageUpdate, onNotify }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [customQuery, setCustomQuery] = useState('');

  useEffect(() => {
    if (isOpen && product) {
      setImages([]);
      setSelectedImage(null);
      setPage(1);
      setHasMore(true);
      setCustomQuery('');
      searchImages(1);
    }
  }, [isOpen, product]);

  const searchImages = async (pageNum = 1, queryOverride = null) => {
    const searchQuery = queryOverride || customQuery || product?.name;
    if (!searchQuery) return;

    if (pageNum === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const query = searchQuery.trim();

      console.log('Buscando imagenes para:', query, 'pagina:', pageNum);

      const response = await fetch('/api/search-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, page: pageNum })
      });

      if (!response.ok) {
        throw new Error('Error al buscar imagenes');
      }

      const data = await response.json();
      console.log('Imagenes encontradas:', data.length);

      if (pageNum === 1) {
        setImages(data || []);
      } else {
        setImages(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore(data && data.length >= 12);
      setPage(pageNum);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleCustomSearch = (e) => {
    e.preventDefault();
    if (customQuery.trim()) {
      setPage(1);
      setImages([]);
      searchImages(1, customQuery.trim());
    }
  };

  const handleLoadMore = () => {
    searchImages(page + 1, customQuery);
  };

  const handleDownloadAndSave = async (imageUrl) => {
    setSelectedImage(imageUrl);
    onClose();

    try {
      const response = await fetch('/api/process-image-from-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: imageUrl, productId: product.id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar la imagen');
      }

      const data = await response.json();
      
      if (onImageUpdate) {
        onImageUpdate(product.id, data.fileName);
      }

      if (onNotify) {
        onNotify('Imagen subida correctamente', 'success');
      }
    } catch (err) {
      console.error(err);
      if (onNotify) {
        onNotify(err.message || 'Error al procesar la imagen', 'error');
      }
    }
  };

  const handleSelectFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      onClose();

      try {
        // El archivo se guarda con el nombre del producto de la lista
        const fileName = nombreArchivoImagen(product.name, file);

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('images')
          .upload(fileName, file, {
            contentType: file.type || 'image/jpeg',
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) {
          throw new Error('Error al subir la imagen: ' + uploadError.message);
        }

        const { error: updateError } = await supabase
          .from('products')
          .update({ image: fileName })
          .eq('id', product.id);

        if (updateError) {
          throw new Error('Error al actualizar el producto: ' + updateError.message);
        }

        if (onImageUpdate) {
          onImageUpdate(product.id, fileName);
        }

        if (onNotify) {
          onNotify('Imagen subida correctamente', 'success');
        }
      } catch (err) {
        console.error(err);
        if (onNotify) {
          onNotify(err.message || 'Error al procesar la imagen', 'error');
        }
      }
    };

    input.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75" />
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <span role="img" aria-label="wand">🪄</span> 
                  Varita Magica - {product?.name}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  Selecciona una imagen de internet o sube una desde tu computadora.
                </p>

                <form onSubmit={handleCustomSearch} className="flex gap-2 mb-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={customQuery}
                      onChange={(e) => setCustomQuery(e.target.value)}
                      placeholder="Buscar con texto personalizado..."
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <svg className="w-5 h-5 absolute right-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <button
                    type="submit"
                    disabled={!customQuery.trim() || isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    Buscar
                  </button>
                </form>

                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
                    <p className="text-gray-500 text-sm">Buscando imagenes...</p>
                  </div>
                ) : (
                  <>
                    {images.length > 0 ? (
                      <div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[50vh] overflow-y-auto p-1">
                          {images.map((img, index) => (
                            <div 
                              key={index}
                              className={`relative group cursor-pointer aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                selectedImage === img.url 
                                  ? 'border-blue-500 shadow-lg' 
                                  : 'border-transparent hover:border-blue-400 hover:shadow-md'
                              }`}
                              onClick={() => handleDownloadAndSave(img.url)}
                            >
                              <img 
                                src={img.previewUrl || img.url} 
                                alt={`Resultado ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all">
                                <span className="text-white text-sm opacity-0 group-hover:opacity-100 font-bold tracking-wide drop-shadow-md bg-blue-600 px-3 py-1 rounded-full">
                                  DESCARGAR
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {hasMore && (
                          <div className="flex justify-center mt-4">
                            <button
                              onClick={handleLoadMore}
                              disabled={isLoadingMore}
                              className="px-6 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-semibold border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                              {isLoadingMore ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                              ) : (
                                'Cargar mas imagenes'
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No se encontraron imagenes.
                      </div>
                    )}
                  </>
                )}

                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={handleSelectFile}
                    className="w-full bg-green-600 text-white px-4 py-3 rounded-md text-sm font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Subir imagen desde mi computadora
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicWandModal;